import { percentToCss, px } from './css.js';
import { clamp as clamp$1, deltaSnap as deltaSnap$1, snap } from './math.js';

/*
 * It is recommended to import this file as:
 *
 * ```ts
 * import * as vec from './vector'
 * ```
 *
 * so that the methods are namespaced and easily recognizable.
 */
const ZERO = vec(0, 0);
const UNIT_X_POS = vec(1, 0);
const UNIT_X_NEG = vec(-1, 0);
const UNIT_Y_POS = vec(0, 1);
const UNIT_Y_NEG = vec(0, -1);
function vec(x, y) {
    return { x, y };
}
function equal(a, b) {
    return a.x === b.x && a.y === b.y;
}
function isZero(v) {
    return equal(v, ZERO);
}
function isInteger(v) {
    return Number.isInteger(v.x) && Number.isInteger(v.y);
}
/**
 * Check if vector `a` is within rounding distance of vector `b`. This means that if `b`
 * were rounded up or down, `a` would be equal to it.
 */
function isWithinRoundingDistance(target, rounded) {
    return (target.x >= Math.floor(rounded.x) &&
        target.x <= Math.ceil(rounded.x) &&
        target.y >= Math.floor(rounded.y) &&
        target.y <= Math.ceil(rounded.y));
}
// =============================================================================
function fromClientXY(e) {
    return vec(e.clientX, e.clientY);
}
/**
 * Get the vector relative to the top-left corner of the given rect.
 * Useful for getting mouse positions relative to an element's bounding box.
 */
function fromClientXYRelative(e, rect) {
    return vec(e.clientX - rect.left, e.clientY - rect.top);
}
function fromMovementXY(e) {
    return vec(e.movementX, e.movementY);
}
function fromDeltaXY(e) {
    return vec(e.deltaX, e.deltaY);
}
function fromWindow() {
    return vec(window.innerWidth, window.innerHeight);
}
function fromElementTopLeft(el) {
    const rect = el.getBoundingClientRect();
    return vec(rect.x, rect.y);
}
function fromElementCenter(el) {
    const rect = el.getBoundingClientRect();
    return vec(rect.left + rect.width / 2, rect.top + rect.height / 2);
}
function fromElementSize(el) {
    const rect = el.getBoundingClientRect();
    return vec(rect.width, rect.height);
}
function fromElementClientSize(el) {
    return vec(el.clientWidth, el.clientHeight);
}
const ARROW_DELTA_MAP = {
    ArrowRight: UNIT_X_POS,
    ArrowLeft: UNIT_X_NEG,
    ArrowDown: UNIT_Y_POS,
    ArrowUp: UNIT_Y_NEG,
};
/**
 * Returns a vector based on all the currently pressed arrow keys. This can be useful for
 * supporting diagonal movement, since you only receive repeat events for the last pressed key,
 * even if multiple keys are currently held.
 *
 * This function assumes the CSS coordinate space, which increases to the right and down.
 *
 * @note If opposite keys are pressed (e.g., left and right), the resulting vector will be zero.
 */
function fromPressedArrowKeys(arrows) {
    return arrows.reduce((acc, arrow) => add(acc, ARROW_DELTA_MAP[arrow]), ZERO);
}
// =============================================================================
function toTranslation(v) {
    return `translate(${v.x}px, ${v.y}px)`;
}
function toTopLeft(v) {
    // Add the `px` here so that this function can also be used on native `style` objects. React
    // has sugar here to allow numbers (which are auto-converted to px values) but `style` doesn't.
    return { top: px(v.y), left: px(v.x) };
}
/** Convert a vector with values in [0, 1] to `top`/`left` CSS percentage strings. */
function toTopLeftPercent(v) {
    return { top: percentToCss(v.y), left: percentToCss(v.x) };
}
function toWidthHeight(v) {
    // Add the `px` here so that this function can also be used on native `style` objects. React
    // has sugar here to allow numbers (which are auto-converted to px values) but `style` doesn't.
    return { width: px(v.x), height: px(v.y) };
}
/** Extract the axis value from the given vector based on the given `orientation`. */
function valueForOrientation(v, orientation) {
    return orientation === 'vertical' ? v.y : v.x;
}
/** Extract the opposite axis value from the given vector based on the given `orientation`. */
function oppositeValueForOrientation(v, orientation) {
    return orientation === 'vertical' ? v.x : v.y;
}
// =============================================================================
function round(v) {
    return vec(Math.round(v.x), Math.round(v.y));
}
/** Snap the given Vector to the nearest point supported by the current device's DPI. */
function dpiSnap(v) {
    const dpi = 1 / (window.devicePixelRatio || 1);
    return vec(snap(v.x, dpi), snap(v.y, dpi));
}
function add(a, b) {
    return vec(a.x + b.x, a.y + b.y);
}
function subtract(a, b) {
    return vec(a.x - b.x, a.y - b.y);
}
function magnitude(v) {
    return Math.hypot(v.x, v.y);
}
function distance(a, b) {
    return magnitude(subtract(a, b));
}
function distanceSquared(a, b) {
    return (a.x - b.x) ** 2 + (a.y - b.y) ** 2;
}
/** Distance from `origin` to an event's `clientX`/`clientY`. Saves an explicit `fromClientXY`. */
function distanceToClientXY(e, origin) {
    return Math.hypot(e.clientX - origin.x, e.clientY - origin.y);
}
/**
 * Finds the shortest distance from a point to the nearest edge of a rect. Points that overlap
 * the rect in one dimension use the cardinal distance to the nearest edge in the other
 * dimension. Otherwise the diagonal distance to the nearest corner is used.
 */
function distanceToRect(v, rect) {
    const dx = Math.max(rect.left - v.x, 0, v.x - rect.right);
    const dy = Math.max(rect.top - v.y, 0, v.y - rect.bottom);
    return Math.hypot(dx, dy);
}
/**
 * Finds the shortest distance from a point to the nearest edge of an element. Points that overlap
 * the element in one dimension use the cardinal distance to the nearest edge in the other
 * dimension. Otherwise the diagonal distance to the nearest corner is used.
 */
function distanceToElement(v, el) {
    return distanceToRect(v, el.getBoundingClientRect());
}
function scale(v, scalar) {
    if (typeof scalar === 'number') {
        return vec(v.x * scalar, v.y * scalar);
    }
    return vec(v.x * scalar.x, v.y * scalar.y);
}
/** Project a vector onto a single axis, zeroing out the other component. */
function project(v, axis) {
    return axis === 'x' ? vec(v.x, 0) : vec(0, v.y);
}
function normalize(v, length = 1) {
    return scale(v, length / magnitude(v));
}
function clamp(v, min, max) {
    return vec(clamp$1(v.x, min.x, max.x), clamp$1(v.y, min.y, max.y));
}
function deltaSnap(v, delta, step) {
    if (typeof step === 'number')
        step = vec(step, step);
    return vec(deltaSnap$1(v.x, delta.x, step.x), deltaSnap$1(v.y, delta.y, step.y));
}
/**
 * Like `deltaSnap`, but leaves an axis untouched when its delta is 0. Useful for arrow-key
 * nudging in 2D, where a value that's between snaps on the perpendicular axis (e.g. from a
 * pointer drag with `precision='infinite'`) shouldn't be silently snapped by an unrelated
 * keypress.
 */
function deltaSnapNonZero(v, delta, step) {
    if (typeof step === 'number')
        step = vec(step, step);
    return vec(delta.x !== 0 ? deltaSnap$1(v.x, delta.x, step.x) : v.x, delta.y !== 0 ? deltaSnap$1(v.y, delta.y, step.y) : v.y);
}
/**
 * Determine where a Vector starting from `start` and ending at `end`
 * exits the given `rect`. Returns null if the Vector did not exit the rect.
 *
 * The strategy is to determine in which direction the Vector exits, calculate the exit points
 * on the two sides this narrows it down to, and then pick the closer of the two.
 */
function exitPoint(start, end, rect) {
    // first, check that the start point is within the rect and the end point is outside
    if (!isWithin(start, rect) || isWithin(end, rect))
        return null;
    // determine the slope intercept form of the line, y = mx + b
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    // we can narrow down which side the line exited based on its direction
    const x1 = dx >= 0 ? rect.right : rect.left;
    const y2 = dy >= 0 ? rect.bottom : rect.top;
    // special case vertical and horizontal lines
    if (dx === 0)
        return vec(start.x, y2);
    if (dy === 0)
        return vec(x1, start.y);
    const m = dy / dx;
    const b = start.y - m * start.x;
    // the point where the line exits horizontally
    const y1 = m * x1 + b;
    const p1 = vec(x1, y1);
    // the point where the line exits vertically
    const x2 = (y2 - b) / m;
    const p2 = vec(x2, y2);
    const d1 = distanceSquared(p1, start);
    const d2 = distanceSquared(p2, start);
    // the exit point is the closer of the two choices
    return d1 < d2 ? p1 : p2;
}
function isWithin(v, rect) {
    return v.x > rect.left && v.x < rect.right && v.y > rect.top && v.y < rect.bottom;
}

export { UNIT_X_NEG, UNIT_X_POS, UNIT_Y_NEG, UNIT_Y_POS, ZERO, add, clamp, deltaSnap, deltaSnapNonZero, distance, distanceSquared, distanceToClientXY, distanceToElement, distanceToRect, dpiSnap, equal, exitPoint, fromClientXY, fromClientXYRelative, fromDeltaXY, fromElementCenter, fromElementClientSize, fromElementSize, fromElementTopLeft, fromMovementXY, fromPressedArrowKeys, fromWindow, isInteger, isWithin, isWithinRoundingDistance, isZero, magnitude, normalize, oppositeValueForOrientation, project, round, scale, subtract, toTopLeft, toTopLeftPercent, toTranslation, toWidthHeight, valueForOrientation, vec };
//# sourceMappingURL=vector.js.map
